import { Box, Button, CircularProgress } from '@mui/material';
import React from 'react';
import Stack from '@mui/material/Stack';
import { Modal } from '../../../../../common/components/Modal/Modal';
import { theme } from '../../../../../common/styles/theme';
import { CheckIcon } from '../../../../../../icons/CheckIcon';
import { CloseIcon } from '../../../../../../icons/CloseIcon';
import { ICallRecordingProgressModalProps } from './types';
import { ConferenceSettingsHOC } from '../../ConferenceSettings/hocs/ConferenceSettingsHOC';
import { TConferenceLogStatus } from '../../ConferenceSettings/components/types';

export function CallRecordingProgressModal({
	open,
	title,
	progressLog,
	isProcessing,
	startedAt,
	stepStartedAt,
	updatedAt,
	audioId,
	settingsSection,
	actions,
	onClose,
	onProcess,
	onCancel,
}: ICallRecordingProgressModalProps) {

	// "Cancelling…" persists through the background rollback: the log line drives it, the local
	// flag only covers the gap before that line appears (onCancel returns instantly).
	const [clickedCancel, setClickedCancel] = React.useState(false);
	const lastLog = progressLog[progressLog.length - 1] || '';
	const cancelling = clickedCancel || lastLog.startsWith('Cancelling');
	const handleCancel = async () => {
		if (cancelling) return;
		setClickedCancel(true);
		try {
			await onCancel?.();
		} finally {
			setClickedCancel(false);
		}
	};

	// scrollIntoView's ancestor lookup got unreliable once the settings panel made the modal taller —
	// walk up to the actual scrolling container and scroll it directly instead.
	const logEndRef = React.useRef<HTMLDivElement | null>(null);
	React.useEffect(() => {
		if (!open) return;
		const el = logEndRef.current;
		if (!el) return;
		let scrollParent: HTMLElement | null = el.parentElement;
		while (scrollParent) {
			const { overflowY } = getComputedStyle(scrollParent);
			if (overflowY === 'auto' || overflowY === 'scroll') break;
			scrollParent = scrollParent.parentElement;
		}
		if (scrollParent) {
			scrollParent.scrollTo({ top: scrollParent.scrollHeight, behavior: 'smooth' });
		} else {
			el.scrollIntoView({ block: 'end', behavior: 'smooth' });
		}
	}, [progressLog.length, open]);

	// One stable interval ticks `now` every second; the ref gate pauses it without recreating it.
	const [now, setNow] = React.useState(() => Date.now());
	const isProcessingRef = React.useRef(isProcessing);
	isProcessingRef.current = isProcessing;
	React.useEffect(() => {
		const interval = setInterval(() => {
			if (isProcessingRef.current) setNow(Date.now());
		}, 1000);
		return () => clearInterval(interval);
	}, []);

	// Live clock while processing; freeze at the last snapshot time so it stops exactly on "Done".
	const reference = isProcessing ? now : (updatedAt ?? now);
	const formatTime = (baseline?: number) => {
		const totalSeconds = baseline ? Math.max(0, Math.floor((reference - baseline) / 1000)) : 0;
		const mm = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
		const ss = (totalSeconds % 60).toString().padStart(2, '0');
		return `${mm}:${ss}`;
	};

	const logStatus: TConferenceLogStatus | null = isProcessing
		? 'running'
		: lastLog.startsWith('Failed')
			? 'failed'
			: lastLog.startsWith('Cancelled')
				? 'cancelled'
				: progressLog.length ? 'done' : null;

	const log = (!!progressLog.length || isProcessing) ? (
		<Stack
			gap={1}
		>
			{progressLog.map((message, i) => {
					const isError = message.startsWith('Failed');
					const isCancelled = message.startsWith('Cancelled');
					const isLast = i === progressLog.length - 1;
					return (
						<Stack
							key={i}
							flexDirection={'row'}
							alignItems={'center'}
							gap={1}
							sx={{
								fontSize: theme.typography.subtitle2.fontSize,
								color: isError
									? theme.palette.error.main
									: isCancelled
										? theme.palette.grey[500]
										: isLast
											? theme.palette.grey[700]
											: theme.palette.grey[400],
							}}
						>
							{isError || isCancelled
								? <CloseIcon
									sx={{
										width: theme.spacing(2),
										height: theme.spacing(2),
										color: isError ? theme.palette.error.main : theme.palette.grey[500],
									}}
								/>
								: (isProcessing && isLast)
									? <CircularProgress size={theme.spacing(2)} />
									: <CheckIcon
										sx={{
											width: theme.spacing(2),
											height: theme.spacing(2),
											color: theme.palette.success.main,
										}}
									/>
							}
							<Box>{message}</Box>

							{isLast &&
								<Box
									sx={{
										fontSize: theme.typography.subtitle1.fontSize,
										color: theme.palette.grey[400],
										fontVariantNumeric: 'tabular-nums',
									}}
								>
									({formatTime(isProcessing ? stepStartedAt : startedAt)})
								</Box>
							}
						</Stack>
					);
				})}

				{isProcessing &&
					<Box
						sx={{
							pl: theme.spacing(3),
							fontSize: theme.typography.subtitle2.fontSize,
							color: theme.palette.grey[400],
							fontVariantNumeric: 'tabular-nums',
						}}
					>
						{formatTime(startedAt)}
					</Box>
				}

				{/* scroll anchor; spacing only with the hint, or it's an empty 32px gap under the settings */}
				<Box ref={logEndRef}
					sx={{
						mt: isProcessing ? theme.spacing(2) : 0,
						pb: isProcessing ? theme.spacing(2) : 0,
						color: theme.palette.grey[400],
						fontSize: theme.typography.subtitle1.fontSize,
						fontWeight: 300,
					}}
				>
					{isProcessing && <>
						Processing can take a few minutes.<br/>
						You can safely close this popup.
					</>}
				</Box>

				{isProcessing && onCancel &&
					<Stack
						flexDirection={'row'}
						justifyContent={'flex-end'}
					>
						<Button
							variant={'outlined'}
							color={'error'}
							disabled={cancelling}
							onClick={handleCancel}
						>
							{cancelling ? 'Cancelling…' : 'Cancel'}
						</Button>
					</Stack>
				}
		</Stack>
	) : null;

	return (
		<Modal
			open={open}
			onClose={onClose}
			title={title}
			PaperProps={{
				sx: {
					width: theme.spacing(80),
					// &&&: outranks Modal's own `> .MuiPaper-root` 90vh caps
					...(settingsSection ? {
						'&&&': { height: '98vh', maxHeight: '98dvh' },
						'&&& > .MuiStack-root': { maxHeight: '100%' },
					} : {}),
				}
			}}
		>
			{/* fragment: Modal's children prop takes a single element */}
			<>
			{settingsSection
				? <ConferenceSettingsHOC
					audioId={audioId}
					isProcessing={isProcessing}
					log={log}
					logStatus={logStatus}
					logTime={formatTime(startedAt)}
					section={settingsSection}
					onProcess={onProcess}
				/>
				: log
			}
			{actions &&
				<Box sx={{ mt: 2 }}>
					{actions}
				</Box>
			}
			</>
		</Modal>
	);
};
